import Enum from 'enum';
import { atou } from 'flow/utils';
import {
    customerIsCention,
    customerIsTelavox,
    getLoggedInUserCustomerKey,
} from 'flow/selectors/selectors';

import { State } from 'flow/types/State';

export const TELAVOX_OMNI_MESSAGE = 'telavox.omni.message';

import icon_call from 'images/omnichannel/omnichannel_call.svg';
import icon_chatbot from 'images/omnichannel/omnichannel_chatbot.svg';
import icon_email from 'images/omnichannel/omnichannel_email.svg';
import icon_facebook from 'images/omnichannel/omnichannel_facebook.svg';
import icon_form from 'images/omnichannel/omnichannel_form.svg';
import icon_generic from 'images/omnichannel/omnichannel_generic.svg';
import icon_google_chat from 'images/omnichannel/omnichannel_google_chat.svg';
import icon_google_play_store from 'images/omnichannel/omnichannel_google_play_store.svg';
import icon_google_review from 'images/omnichannel/omnichannel_google_review.svg';
import icon_instagram from 'images/omnichannel/omnichannel_instagram.svg';
import icon_jira from 'images/omnichannel/omnichannel_jira.svg';
import icon_line from 'images/omnichannel/omnichannel_line.svg';
import icon_linkedin from 'images/omnichannel/omnichannel_linkedin.svg';
import icon_chat from 'images/omnichannel/omnichannel_live_chat.svg';
import icon_messenger from 'images/omnichannel/omnichannel_messenger.svg';
import icon_ms_teams from 'images/omnichannel/omnichannel_ms_teams.svg';
import icon_slack from 'images/omnichannel/omnichannel_slack.svg';
import icon_sms from 'images/omnichannel/omnichannel_sms.svg';
import icon_telegram from 'images/omnichannel/omnichannel_telegram.svg';
import icon_trustpilot from 'images/omnichannel/omnichannel_trustpilot.svg';
import icon_twilio from 'images/omnichannel/omnichannel_twilio.svg';
import icon_twitter from 'images/omnichannel/omnichannel_twitter.svg';
import icon_viber from 'images/omnichannel/omnichannel_viber.svg';
import icon_vkontakte from 'images/omnichannel/omnichannel_vkontakte.svg';
import icon_whatsapp from 'images/omnichannel/omnichannel_whatsapp.svg';
import icon_youtube from 'images/omnichannel/omnichannel_youtube.svg';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { IconKey, isIconKey } from 'flow/components/ui-components/Icon';
import {
    AuthenticationDataMap,
    BagMessage,
    OmniMessage,
    OmniMessageEventData,
    TopEvent,
} from 'flow/types/Omnichannel';
import { RTCSession } from 'flow/types/RTCSession';

export const channelIcons: { [key: string]: string } = {
    call: icon_call,
    chat: icon_chat,
    chat_bot: icon_chatbot,
    email: icon_email,
    facebook: icon_facebook,
    form: icon_form,
    generic: icon_generic,
    google_chat: icon_google_chat,
    google_play_store: icon_google_play_store,
    google_review: icon_google_review,
    instagram: icon_instagram,
    instagram_manual: icon_instagram,
    jira: icon_jira,
    line: icon_line,
    linkedin: icon_linkedin,
    linkedin_manual: icon_linkedin,
    messenger: icon_messenger,
    microsoft_teams: icon_ms_teams,
    slack: icon_slack,
    sms: icon_sms,
    sms_manual: icon_sms,
    telegram: icon_telegram,
    trustpilot: icon_trustpilot,
    twilio: icon_twilio,
    twitter: icon_twitter,
    twitter_manual: icon_twitter,
    viber: icon_viber,
    vkontakte: icon_vkontakte,
    vkontakte_manual: icon_vkontakte,
    voice: icon_call,
    voice_manual: icon_call,
    whatsapp: icon_whatsapp,
    whatsapp_manual: icon_whatsapp,
    youtube: icon_youtube,
    youtube_manual: icon_youtube,
};

export const ACTIONS = new Enum({
    GO_TO_ERRAND: 'open-errand',
    SUBSCRIBE_TO_USER_OWNED_ERRANDS_COUNT: 'subscribe-errands-count',
    USER_SESSION_READY: 'user-session-ready',
    USER_ERRANDS_COUNT: 'user-errands-count-changed',
    AUTHENTICATE: 'authenticate',
    READY_FOR_AUTHENTICATION: 'ready',
    ERROR: 'error',
});

export const getSubscribeToUserOwnedErrandsCountPayload = () => {
    return Object.freeze({
        action: ACTIONS.SUBSCRIBE_TO_USER_OWNED_ERRANDS_COUNT.value,
    });
};

export const getGoToOmniChannelErrandPayload = (deepLinkId: string) => {
    return Object.freeze({
        action: ACTIONS.GO_TO_ERRAND.value,
        params: {
            deepLink: deepLinkId,
        },
    });
};

export const getAuthenticateUserPayload = (
    accessKeyId: string,
    timestamp: number,
    workspace: string,
    signature: string,
) => {
    return Object.freeze({
        action: ACTIONS.AUTHENTICATE.value,
        params: {
            access_key_id: accessKeyId,
            timestamp: timestamp,
            workspace: workspace,
            signature: signature,
        },
    });
};

export const dataIsOfTypeUserOwnedOmnichannelErrandsCount = (
    data: OmniMessageEventData,
) => {
    return data?.action === ACTIONS.USER_ERRANDS_COUNT.value;
};

export const dataIsOfTypeUserSessionReady = (data: OmniMessageEventData) => {
    return data?.action === ACTIONS.USER_SESSION_READY.value;
};

export const dataIsOfTypeCentionIsReadyToReceiveAuthenticationData = (
    data: OmniMessageEventData,
) => {
    return data?.action === ACTIONS.READY_FOR_AUTHENTICATION.value;
};

export const dataIsOfTypeError = (data: OmniMessageEventData) => {
    return data?.action === ACTIONS.ERROR.value;
};

export const dataIsOfTypeAuthenticationError = (data: OmniMessageEventData) => {
    return dataIsOfTypeError(data) && data.params?.reply === 'authenticate';
};

export const getErrorMessageFromData = (data: OmniMessageEventData) => {
    return data.params?.error;
};

export const getErrandsCountFromData = (data: OmniMessageEventData) => {
    return data.params?.errandsCount;
};

export const rtcSessionRepresentsCentionErrand = (rtcSession: RTCSession) => {
    try {
        return !!rtcSession._request.headers['X-Tvx-Omni-Message'];
    } catch (_error) {
        return false;
    }
};

export const getCentionDataFromRtcSession = (
    rtcSession: RTCSession,
    propertyName: string,
) => {
    if (!rtcSession || !rtcSession._request) {
        return undefined;
    }

    const headerValues = rtcSession._request.headers['X-Tvx-Omni-Message'];

    try {
        const firstValueJson = atou(headerValues[0]['raw']);
        const firstMessageParsed = JSON.parse(firstValueJson);

        return firstMessageParsed[propertyName];
    } catch (_error) {
        return undefined;
    }
};

export const topEventHasChannelOfTypeCentionOmni = (event?: TopEvent) => {
    return event?.Channel?.toLowerCase().startsWith('omni/') ?? false;
};

export const getBagVariableCentionMessageFromTopMessage = (
    message?: BagMessage,
) => {
    if (!message?.VariableCount) {
        return undefined;
    }

    try {
        const count = parseInt(message.VariableCount);
        let omniMessage: OmniMessage;

        for (let i = 0; i < count; i++) {
            const name = message[`VariableName${i}`];
            const type = message[`VariableType${i}`];

            if (name !== TELAVOX_OMNI_MESSAGE || type !== 'JSON') {
                continue;
            }

            const value = message[`VariableValue${i}`];
            omniMessage = JSON.parse(atou(value));

            break;
        }

        return omniMessage;
    } catch (_error) {
        return undefined;
    }
};

export const authenticationDataIsInsufficient = (
    data: AuthenticationDataMap,
) => {
    if (!data || data.isEmpty()) {
        return true;
    }

    const accessKeyId = data.get('accessKeyId');
    const timestamp = data.get('timestamp');
    const workspace = data.get('workspace');
    const signature = data.get('signature');

    return !accessKeyId || !timestamp || !workspace || !signature;
};

const centionTestUsers = [
    'extension-3665942',
    'extension-3665944',
    'extension-3665946',
    'extension-3665948',
    'extension-3666165',
    'extension-3666167',
];

export const extensionWithKeyIsCentionTestUser = (
    extensionKey: ExtensionKey,
) => {
    return centionTestUsers.includes(extensionKey);
};

export const getChannelStringConverted = (channel: string) => {
    return channel && channel.replace(/\s/g, '_').replace(/[()]/g, '');
};

export const getOmnichannelChannelTypeIcon = (channel: string) => {
    const channelProperty = getChannelStringConverted(channel);
    return channelIcons[channelProperty] || channelIcons['generic'];
};

export const normalizeOmnichannelChannelType = (channel) => {
    if (channel.includes('email')) {
        return 'email';
    }

    return getChannelStringConverted(channel);
};

export const getOmnichannelChannelTypeIconKey = (
    channel: string,
): IconKey | undefined => {
    const normalized = channel && normalizeOmnichannelChannelType(channel);
    return isIconKey(normalized) ? normalized : undefined;
};

const CENTION_URL = 'https://cloud.cention.com';
const CENTION_QUALITY_ASSURANCE_URL = 'https://cloud-qa.cention.com';

const userShouldBePointedToQualityAssuranceUrl = (state: State) => {
    return (
        customerIsTelavox(state) ||
        customerIsCention(state) ||
        getLoggedInUserCustomerKey(state) === 'customer-282269' //QAPreviewCustomer
    );
};

export const getCentionUrl = (state: State) => {
    return userShouldBePointedToQualityAssuranceUrl(state)
        ? CENTION_QUALITY_ASSURANCE_URL
        : CENTION_URL;
};
